import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';

const SUPPORTED_LOCALES = ['el', 'en', 'fr', 'es', 'de', 'pt', 'tr', 'it', 'ru', 'sq', 'bg', 'zh', 'hi', 'ar'];
const LOCALE_LABELS = {
  el: 'Ελληνικά', en: 'English', fr: 'Français', es: 'Español',
  de: 'Deutsch', pt: 'Português', tr: 'Türkçe', it: 'Italiano',
  ru: 'Русский', sq: 'Shqip', bg: 'Български', zh: '中文',
  hi: 'हिन्दी', ar: 'العربية',
};

const FALLBACK_TRANSLATIONS = {
  'nav.home': 'Home', 'nav.about': 'About', 'nav.programs': 'Programs',
  'nav.admissions': 'Admissions', 'nav.seminars': 'Seminars',
  'nav.testimonials': 'Success Stories', 'nav.faq': 'FAQ', 'nav.contact': 'Contact',
  'nav.login': 'Sign In', 'nav.apply': 'Apply Now', 'nav.portal': 'My Portal',
  'nav.admin': 'Admin', 'nav.logout': 'Sign Out', 'nav.magazine': 'Magazine',
  'home.hero_title': 'Master the Art of Hair',
  'home.hero_subtitle': 'Where artistry meets precision. Where passion becomes profession.',
  'home.cta_apply': 'Begin Your Journey',
  'home.cta_consult': 'Book a Consultation',
  'home.programs_title': 'Our Programs',
  'home.programs_subtitle': 'World-class education for every stage of your career',
  'home.why_title': 'Why MISEL GROUP',
  'home.testimonials_title': 'Voices of Transformation',
  'home.stats_students': 'Graduates Worldwide',
  'home.stats_countries': 'Countries Represented',
  'home.stats_years': 'Years of Excellence',
  'home.stats_satisfaction': 'Student Satisfaction',
  'common.learn_more': 'Learn More',
  'common.apply_now': 'Apply Now',
  'common.book_consultation': 'Book a Consultation',
  'common.contact_us': 'Contact Us',
  'common.view_programs': 'View Programs',
  'common.duration': 'Duration',
  'common.tuition': 'Tuition',
  'common.start_date': 'Next Start Date',
  'common.from': 'From',
  'common.per_month': '/ month',
  'common.loading': 'Loading...',
  'footer.tagline': 'The international academy where artistry meets precision.',
  'footer.programs': 'Programs', 'footer.company': 'Company', 'footer.connect': 'Connect',
  'home.magazine_title': 'Latest from the Magazine',
  'home.magazine_view_all': 'View All Articles',
  'portal.title': 'My Portal',
  'portal.welcome': 'Welcome, {name}',
  'portal.back_to_site': 'Back to Site',
  'portal.application': 'Application',
  'portal.documents': 'Documents',
  'portal.payments': 'Payments',
  'portal.attendance': 'Attendance',
  'portal.grades': 'Grades',
  'portal.requests': 'Requests',
  'portal.finances': 'Finances',
  'portal.my_finances': 'My Finances',
  'portal.my_program': 'My Program',
  'portal.status': 'Status',
  'portal.enrolled': 'Enrolled',
  'portal.remaining': 'Remaining',
  'auth.sign_in': 'Sign In',
  'auth.signing_in': 'Signing In...',
  'auth.email': 'Email',
  'auth.password': 'Password',
  'auth.login_subtitle': 'Sign in to your MISEL account',
  'auth.no_account': "Don't have an account?",
  'auth.create_account': 'Create one',
  'auth.welcome_back': 'Welcome back, {name}!',
};

const LocaleContext = createContext(null);

export function LocaleProvider({ children }) {
  const getInitialLocale = () => {
    const saved = localStorage.getItem('misel_locale');
    if (saved && SUPPORTED_LOCALES.includes(saved)) return saved;
    const seg = window.location.pathname.split('/')[1];
    return SUPPORTED_LOCALES.includes(seg) ? seg : 'en';
  };

  const [locale, setLocaleState] = useState(getInitialLocale);
  const [translations, setTranslations] = useState(FALLBACK_TRANSLATIONS);
  const [loaded, setLoaded] = useState(false);

  const setLocale = useCallback((newLocale) => {
    if (SUPPORTED_LOCALES.includes(newLocale)) {
      setLocaleState(newLocale);
      localStorage.setItem('misel_locale', newLocale);
    }
  }, []);

  useEffect(() => {
    const fetchTranslations = async () => {
      try {
        const res = await api.get(`/translations/${locale}`);
        if (res.data?.translations && Object.keys(res.data.translations).length > 0) {
          setTranslations({ ...FALLBACK_TRANSLATIONS, ...res.data.translations });
        }
      } catch (err) {
        console.warn('[i18n] Failed to load translations');
      }
      setLoaded(true);
    };
    fetchTranslations();
  }, [locale]);

  const t = useCallback((key, fallback) => {
    return translations[key] || fallback || key;
  }, [translations]);

  return (
    <LocaleContext.Provider value={{ locale, setLocale, t, loaded, locales: SUPPORTED_LOCALES, localeLabels: LOCALE_LABELS }}>
      {loaded ? children : (
        <div className="min-h-screen flex items-center justify-center bg-[#FAFAFA]">
          <div className="animate-pulse text-muted-foreground text-sm">MISEL GROUP</div>
        </div>
      )}
    </LocaleContext.Provider>
  );
}

export const useLocale = () => {
  const context = useContext(LocaleContext);
  if (!context) {
    // Fallback when context is not yet available
    return {
      locale: 'en',
      setLocale: () => {},
      t: (key, fallback) => fallback || key,
      loaded: false,
      locales: SUPPORTED_LOCALES,
      localeLabels: LOCALE_LABELS,
    };
  }
  return context;
};
export { SUPPORTED_LOCALES, LOCALE_LABELS };
